import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { apiService } from 'src/utils/apiServices';
import { PHYSICAL_API, authHeaders, physicalErrorMessage } from 'src/utils/physical-api';

const HtsDataContext = createContext({ htsCodes: [], loading: false });
const HtsActionsContext = createContext({});

export const HtsProvider = ({ children }) => {
  const [htsCodes, setHtsCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  const getHtsCodes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiService.get(PHYSICAL_API.hts, { headers: authHeaders() });
      setHtsCodes(data ?? []);
      return data ?? [];
    } catch (error) {
      toast.error(physicalErrorMessage(error, 'Could not load HTS codes'));
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const saveHts = useCallback(
    async (id, hts) => {
      try {
        const { data } = id
          ? await apiService.patch(`${PHYSICAL_API.hts}/${id}`, hts, { headers: authHeaders() })
          : await apiService.post(PHYSICAL_API.hts, hts, { headers: authHeaders() });
        toast.success(id ? 'HTS code updated!' : 'HTS code created!');
        await getHtsCodes();
        return data;
      } catch (error) {
        toast.error(physicalErrorMessage(error, 'HTS code not saved'));
        return null;
      }
    },
    [getHtsCodes]
  );

  const deleteHts = useCallback(async (id) => {
    try {
      await apiService.delete(`${PHYSICAL_API.hts}/${id}`, { headers: authHeaders() });
      setHtsCodes((prev) => prev.filter((hts) => hts.id !== id));
      toast.success('HTS code deleted!');
      return true;
    } catch (error) {
      toast.error(physicalErrorMessage(error, 'HTS code not deleted'));
      return false;
    }
  }, []);

  const importHts = useCallback(
    async (file) => {
      const body = new FormData();
      body.append('file', file);
      try {
        const { data } = await apiService.post(`${PHYSICAL_API.hts}/import`, body, {
          headers: authHeaders(),
          timeout: 60000,
        });
        await getHtsCodes();
        return data;
      } catch (error) {
        toast.error(physicalErrorMessage(error, 'Import failed'));
        return null;
      }
    },
    [getHtsCodes]
  );

  const data = useMemo(() => ({ htsCodes, loading }), [htsCodes, loading]);
  const actions = useMemo(
    () => ({ getHtsCodes, saveHts, deleteHts, importHts }),
    [getHtsCodes, saveHts, deleteHts, importHts]
  );

  return (
    <HtsDataContext.Provider value={data}>
      <HtsActionsContext.Provider value={actions}>{children}</HtsActionsContext.Provider>
    </HtsDataContext.Provider>
  );
};

HtsProvider.propTypes = {
  children: PropTypes.node,
};

export const useHtsData = () => useContext(HtsDataContext);
export const useHtsActions = () => useContext(HtsActionsContext);
